import I18n from 'common/i18n'
import Input from 'components/ui/form/input'
import Button, { BlueButton } from 'components/ui/button'
import Form from 'components/ui/form'
import Password from 'components/ui/form/password'
import { LoginFormInterface } from './interfaces'
import { useEffect, useState } from 'react'
import CircularProgress from '@mui/material/CircularProgress'
import { useAuth } from 'common/auth/auth-provider'
import Alert from '@mui/material/Alert'
import Box from '@mui/system/Box'
import { useSearchParams } from 'react-router-dom'
import Divider from '@mui/material/Divider'
import GoogleAnalytics from 'common/utils/google-analytics'

export default function LoginForm({
  onClickForgotPasswordButton,
  onClickCreateUserButton,
  isAdminModule
}: LoginFormInterface) {
  const [state, setState] = useState({ loading: false, error: '' })
  const [, setSearchParams] = useSearchParams()
  const [data, setData] = useState({ email: '', password: '' })
  const auth = useAuth()
  const login = async function (email: string, password: string) {
    try {
      setState((prev) => ({ ...prev, loading: true, error: '' }))
      await auth.login(email, password)

      // Rastreia login bem-sucedido
      GoogleAnalytics.trackEvent('login', {
        method: 'email',
        module: isAdminModule ? 'admin' : 'site'
      })
    } catch (error: any) {
      let errorMessage = 'Erro ao fazer login. Tente novamente.'

      // Handle specific error types from server response
      if (error.message) {
        if (
          error.message.includes('Credenciais inválidas') ||
          error.message.includes('InvalidCredentialsException') ||
          error.message.includes('NotAuthorizedException')
        ) {
          errorMessage = 'E-mail ou senha incorretos.'
        } else if (
          error.message.includes('Usuário não encontrado') ||
          error.message.includes('UserNotFoundExistsException')
        ) {
          errorMessage = 'Usuário não encontrado. Verifique seu e-mail.'
        } else if (error.message.includes('Failed to execute')) {
          // This is likely a 401 error with empty response, so it's an authentication error
          errorMessage = 'E-mail ou senha incorretos.'
        } else {
          errorMessage = error.message
        }
      }

      setState((prev) => ({
        ...prev,
        loading: false,
        error: errorMessage
      }))

      // Rastreia erro de login
      GoogleAnalytics.trackEvent('login_error', {
        error_type: errorMessage,
        module: isAdminModule ? 'admin' : 'site'
      })

      // Keep the form data when there's an error
      setData((prev) => ({ ...prev, password: '' })) // Only clear password, keep email
    } finally {
      setState((prev) => ({ ...prev, loading: false }))
    }
  }

  useEffect(() => {
    setSearchParams({})
  }, [setSearchParams])

  return (
    <>
      {
        <>
          {state.loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress color='primary' />
            </Box>
          )}
        </>
      }
      <Box sx={state.loading ? { display: 'none' } : {}}>
        <Form
          onSubmit={(formData) => {
            const { email, password } = formData
            login(email.toString(), password.toString())
          }}
          autocomplete={false}
        >
          {
            <>
              {Boolean(state.error) && (
                <Alert
                  severity='error'
                  sx={{
                    mb: 3,
                    fontSize: {
                      xs: '0.875rem',
                      sm: '1rem'
                    }
                  }}
                >
                  {state.error}
                </Alert>
              )}
            </>
          }

          <Box sx={{ mb: 2 }}>
            <Input
              id='email'
              type='email'
              name='email'
              searchBorder={true}
              label={I18n.general.email}
              placeholder={I18n.general.email}
              required={true}
              onChange={(value) =>
                setData((prev) => ({ ...prev, email: value }))
              }
              value={data.email}
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <Password
              id='password'
              name='password'
              label={I18n.login.password}
              placeholder={I18n.login.password}
              required={true}
              value={data.password}
              onChange={(value) =>
                setData((prev) => ({ ...prev, password: value }))
              }
            />
          </Box>

          <Box sx={{ mb: 2 }}>
            <BlueButton
              variant={'contained'}
              type='submit'
              sx={{
                width: '100%',
                fontSize: {
                  xs: '0.875rem',
                  sm: '1rem'
                },
                padding: {
                  xs: '10px 16px',
                  sm: '12px 24px'
                }
              }}
            >
              {I18n.login.signin}
            </BlueButton>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Button
              variant={'text'}
              sx={{
                width: '100%',
                fontSize: {
                  xs: '0.75rem',
                  sm: '0.875rem'
                },
                padding: {
                  xs: '8px 16px',
                  sm: '10px 20px'
                }
              }}
              onClick={(e) => {
                GoogleAnalytics.trackEvent('forgot_password_click')
                onClickForgotPasswordButton(e)
              }}
            >
              {I18n.login.forgotPassword}
            </Button>
          </Box>

          {!isAdminModule ? (
            <>
              <Box sx={{ mb: 2 }}>
                <Divider variant='middle' />
              </Box>
              <Box>
                <Button
                  variant={'outlined'}
                  sx={{
                    width: '100%',
                    color: '#457B9D',
                    borderColor: '#457B9D',
                    fontSize: {
                      xs: '0.875rem',
                      sm: '1rem'
                    },
                    padding: {
                      xs: '10px 16px',
                      sm: '12px 24px'
                    },
                    '&:hover': {
                      color: '#457B9D',
                      borderColor: '#457B9D',
                      backgroundColor: 'transparent'
                    }
                  }}
                  onClick={(e) => {
                    GoogleAnalytics.trackEvent('create_user_click')
                    onClickCreateUserButton(e)
                  }}
                >
                  {I18n.login.createUser}
                </Button>
              </Box>
            </>
          ) : (
            <></>
          )}
        </Form>
      </Box>
    </>
  )
}
